/*
 * PARTPOINT — /search results · pp-search-results.js · v1.2.0 (2026-10-08: refine chips / suggestions use the text the
 * engine checked and counted — the number on a chip is the number its page shows; no suggestion repeats the query;
 * 1.1.0 2026-10-02: one 6 s deadline shared with the inline pending script — no late card swap; first query read in a
 * task of its own; 1.0.0 2026-09-24, OPUS search audit S1–S7)
 *
 * Renders the /search page from the complete published catalog with the shared engine (pp-search-core.js):
 * the same query gives the same products from the form, a shared URL, the suggestions or a refinement chip.
 * Hard compatibility (model, year inside the product's own range, part, side, position, quality, LED, SKU) decides
 * the main list; products whose data is silent are listed apart as „to be confirmed“, pieces/accessories of the
 * part as „related“. Nothing is invented: when nothing fits, the page says why and offers what does exist.
 *
 * Progressive: Shopify renders its own results first (hidden while the engine loads, visible without JS, on an
 * old browser, or when the index is not complete — [data-pp-native]). User text only ever reaches the DOM through
 * textContent/attributes; product links are same-origin /products/<encoded handle>.
 */
(function () {
  'use strict';
  const root = document.getElementById('pp-sr');
  if (!root) return;
  const html = document.documentElement;
  let cfg;
  try { cfg = JSON.parse(document.getElementById('pp-sr-config').textContent); } catch (e) { html.classList.remove('pp-sr-pending'); return; }
  const lang = cfg.lang || (html.lang || 'ka').split('-')[0];
  const PAGE = 48, GROUP = 24;
  const state = { core: null, index: null, q: '', sort: 'relevance', shown: PAGE, result: null, gen: 0 };
  let fellBack = false;

  // 1.1.0: one deadline for both scripts. The inline script in main-search.liquid showed Shopify's own results at
  // 6 s while this fallback waited until 9 s, so an index arriving between 6 and 9 s swapped visible cards under the
  // customer's finger. Now this script takes over the inline timer and falls back at the same 6 s mark (counted from
  // the inline script, i.e. from the start of the page); the inline timer stays only for when this file never runs.
  const DEADLINE = 6000;
  const t0 = typeof window.ppSrT0 === 'number' ? window.ppSrT0 : 0;
  if (window.ppSrPendingTimer) clearTimeout(window.ppSrPendingTimer);
  const safety = setTimeout(() => fallback('timeout'), Math.max(0, t0 + DEADLINE - performance.now()));
  function fallback(reason) {
    if (state.index || fellBack) return;
    fellBack = true;
    clearTimeout(safety);
    html.classList.remove('pp-sr-pending');
    html.classList.remove('pp-sr-on');
    root.hidden = true;
    root.dataset.fallback = String(reason || 'error').slice(0, 40);
    enhanceNativeLoadMore();
  }

  function start() {
    const params = new URL(location.href).searchParams;
    const q = (params.get('q') || '').slice(0, 400);
    if (!q.trim() || !window.PPSearch) { fallback(q.trim() ? 'no-loader' : 'empty'); return; }
    window.PPSearch.ensureIndex().then(({ core, index }) => {
      if (fellBack) return;
      clearTimeout(safety);
      state.core = core; state.index = index;
      readUrl();
      // 1.1.0: the first query of a page pays one-time costs (pattern compilation, first runs of the engine's
      // functions, the collator behind localeCompare). Reading the query once in a task of its own splits that
      // from the render task below; parseQuery is pure, so the results are exactly the same.
      return new Promise(resolve => setTimeout(resolve, 0)).then(() => {
        try { core.parseQuery(state.q, index); 'a'.localeCompare('b'); } catch (e) { /* render() reports real errors */ }
        return new Promise(resolve => setTimeout(resolve, 0));
      }).then(() => show());
    }).catch(err => fallback(err && err.message));
  }

  function show() {
    html.classList.add('pp-sr-on');
    html.classList.remove('pp-sr-pending');
    root.hidden = false;
    render(true, history.state && history.state.ppShown);
    history.replaceState(Object.assign({}, history.state, { ppSr: 1, ppShown: state.shown }), '');
    window.addEventListener('popstate', () => { readUrl(); render(false, history.state && history.state.ppShown); });
  }

  function readUrl() {
    const params = new URL(location.href).searchParams;
    state.q = (params.get('q') || '').slice(0, 400);
    const sort = params.get('sort_by') || cfg.sort || 'relevance';
    state.sort = cfg.sorts.some(s => s.value === sort) ? sort : 'relevance';
    state.shown = PAGE;
    document.querySelectorAll('input[name="q"][type="search"]').forEach(input => { if (document.activeElement !== input) input.value = state.q; });
  }
  function urlFor(q, sort) {
    const url = new URL(location.href);
    url.searchParams.set('q', q);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.delete('page');
    if (sort && sort !== 'relevance') url.searchParams.set('sort_by', sort); else url.searchParams.delete('sort_by');
    [...url.searchParams.keys()].filter(k => k.indexOf('filter.') === 0).forEach(k => url.searchParams.delete(k));
    return url;
  }
  function go(q, sort, focusTarget) {
    const url = urlFor(q, sort);
    history.pushState({ ppSr: 1, ppShown: PAGE }, '', url);
    readUrl();
    render(false);
    const target = focusTarget && root.querySelector(focusTarget);
    (target || root.querySelector('.pp-sr__count')).focus({ preventScroll: false });
  }

  /* ------------------------------------------------------------------ helpers */
  function el(tag, cls, text) {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }
  function msg(key, params) { return state.core.msg(lang, key, params); }
  function t(key) { return (cfg.labels && cfg.labels[key]) || key; }
  function chip(label, href, opts) {
    const a = el('a', 'pp-sr-chip' + (opts && opts.on ? ' pp-sr-chip--on' : ''));
    a.href = href;
    a.append(el('span', 'pp-sr-chip__label', label));
    if (opts && opts.count != null) a.append(el('span', 'pp-sr-chip__count', String(opts.count)));
    if (opts && opts.on) { a.append(el('span', 'pp-sr-chip__x', '×')); a.setAttribute('aria-label', msg('remove', { x: label })); }
    a.addEventListener('click', event => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      go(opts.q, state.sort);
    });
    return a;
  }

  /* ------------------------------------------------------------------ cards (card-product.liquid parity) */
  function card(item, eager) {
    const core = state.core, index = state.index, c = cfg.card || {};
    const li = el('li', 'grid__item');
    const wrap = el('div', 'card-wrapper product-card-wrapper underline-links-hover');
    const hasImg = !!item.img;
    const cardEl = el('div', 'card card--' + (c.style || 'standard') + (hasImg ? ' card--media' : ' card--text') + (c.style === 'card' ? ' color-' + (c.scheme || 'scheme-2') + ' gradient' : ''));
    cardEl.style.setProperty('--ratio-percent', '100%');
    const inner = el('div', 'card__inner' + (c.style !== 'card' ? ' color-' + (c.scheme || 'scheme-2') + ' gradient' : '') + ' ratio');
    inner.style.setProperty('--ratio-percent', '100%');
    const url = core.productUrl(item, index.localeRoot);
    const idBase = 'pp-sr-' + item.id;
    if (hasImg) {
      const media = el('div', 'card__media');
      const m = el('div', 'media media--transparent media--hover-effect');
      const img = document.createElement('img');
      const widths = [165, 360, 533, 720].filter(w => !item.imgW || item.imgW >= w);
      const srcset = widths.map(w => core.imageUrl(item, w) + ' ' + w + 'w');
      if (item.imgW) srcset.push(core.imageUrl(item, 0) + ' ' + item.imgW + 'w');
      img.srcset = srcset.join(', ');
      img.src = core.imageUrl(item, 533);
      const pw = Number(c.page_width) || 1200;
      img.sizes = '(min-width: ' + pw + 'px) ' + Math.round((pw - 130) / 4) + 'px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)';
      img.alt = item.title;
      img.className = 'motion-reduce';
      img.width = item.imgW || 400; img.height = item.imgW || 400;
      if (eager) img.setAttribute('fetchpriority', 'high'); else img.loading = 'lazy';
      img.decoding = 'async';
      m.append(img);
      const q = item.a.quality;
      if (item.vendor === 'Orginal' || q === 'orig') m.append(el('div', 'product-badge product-badge--orig', t('original')));
      else if (item.vendor === 'Tong Yang') m.append(el('div', 'product-badge product-badge--tong', 'Tong Yang'));
      else if (item.vendor === 'China') m.append(el('div', 'product-badge product-badge--analog', t('analog')));
      media.append(m);
      inner.append(media);
    }
    const innerContent = el('div', 'card__content');
    const innerInfo = el('div', 'card__information');
    const h3a = el('h3', 'card__heading');
    const a1 = el('a', 'full-unstyled-link', item.title);
    a1.href = url; a1.tabIndex = -1; a1.setAttribute('aria-hidden', 'true');
    h3a.append(a1); innerInfo.append(h3a); innerContent.append(innerInfo);
    const badge = el('div', 'card__badge ' + (c.badge_position || 'bottom left'));
    if (!item.available) { const b = el('span', 'badge badge--bottom-left color-' + (c.sold_out_scheme || 'scheme-3'), t('sold_out')); b.id = idBase + '-badge'; badge.append(b); }
    else if (item.compare > item.price) { const b = el('span', 'badge badge--bottom-left color-' + (c.sale_scheme || 'scheme-4'), t('on_sale')); b.id = idBase + '-badge'; badge.append(b); }
    innerContent.append(badge);
    inner.append(innerContent);
    cardEl.append(inner);
    const content = el('div', 'card__content');
    const info = el('div', 'card__information');
    const h3 = el('h3', 'card__heading' + (hasImg || c.style === 'standard' ? ' h5' : ''));
    const a2 = el('a', 'full-unstyled-link', item.title);
    a2.href = url; a2.id = idBase + '-link';
    a2.setAttribute('aria-labelledby', idBase + '-link' + (badge.firstChild ? ' ' + idBase + '-badge' : ''));
    h3.append(a2); info.append(h3);
    const ci = el('div', 'card-information');
    ci.append(price(item));
    if (item.unconfirmed) ci.append(el('p', 'pp-sr-card-note', msg('toConfirm') + (item.unconfirmed.length ? ': ' + item.unconfirmed.map(k => msg('unknown_' + k)).join(', ') : '')));
    info.append(ci); content.append(info);
    cardEl.append(content);
    wrap.append(cardEl); li.append(wrap);
    return li;
  }
  function price(item) {
    const core = state.core, money = state.index.money;
    const sale = item.compare > item.price;
    const box = el('div', 'price' + (sale ? ' price--on-sale' : ''));
    const cont = el('div', 'price__container');
    const reg = el('div', 'price__regular');
    reg.append(el('span', 'visually-hidden visually-hidden--inline', t('regular_price')), el('span', 'price-item price-item--regular', core.formatMoney(item.price, money)));
    const saleEl = el('div', 'price__sale');
    const s = el('s', 'price-item price-item--regular', sale ? core.formatMoney(item.compare, money) : '');
    const sWrap = el('span'); sWrap.append(s);
    saleEl.append(el('span', 'visually-hidden visually-hidden--inline', t('regular_price')), sWrap, el('span', 'visually-hidden visually-hidden--inline', t('sale_price')), el('span', 'price-item price-item--sale price-item--last', core.formatMoney(item.price, money)));
    cont.append(reg, saleEl); box.append(cont);
    return box;
  }
  function grid(items, from, eagerFirst) {
    const frag = document.createDocumentFragment();
    items.forEach((item, i) => frag.append(card(item, eagerFirst && from + i < 2)));
    return frag;
  }
  function listEl(id) {
    const ul = el('ul', 'grid product-grid grid--' + ((cfg.card && cfg.card.cols_mobile) || '2') + '-col-tablet-down grid--' + ((cfg.card && cfg.card.cols_desktop) || 4) + '-col-desktop');
    ul.setAttribute('role', 'list');
    if (id) ul.id = id;
    return ul;
  }

  /* ------------------------------------------------------------------ render */
  function render(initial, restoreShown) {
    const core = state.core, index = state.index;
    const gen = ++state.gen;
    const result = core.search(index, state.q, { sort: state.sort, lang });
    state.result = result;
    if (restoreShown && restoreShown > PAGE) state.shown = Math.min(restoreShown, result.main.length);
    const intent = result.intent;
    const body = el('div', 'pp-sr__body');

    // toolbar: live count + sort
    const bar = el('div', 'pp-sr__bar');
    const count = el('h2', 'pp-sr__count');
    count.tabIndex = -1;
    count.id = 'pp-sr-count';
    count.textContent = result.mode === 'clarify' ? msg('matching', { n: result.total }) : msg('results', { n: result.total });
    bar.append(count);
    if (result.main.length > 1) {
      const label = el('label', 'pp-sr__sort');
      const span = el('span', null, msg('sort'));
      const select = el('select', 'pp-sr__select');
      select.id = 'pp-sr-sort';
      cfg.sorts.forEach(s => { const o = el('option', null, s.label); o.value = s.value; if (s.value === state.sort) o.selected = true; select.append(o); });
      select.addEventListener('change', () => go(state.q, select.value, '#pp-sr-sort'));
      label.append(span, select);
      bar.append(label);
    }
    body.append(bar);

    // what the engine understood — each condition removable
    const understood = core.describeIntent(intent, lang);
    if (understood.length && result.mode !== 'sku') {
      const row = el('div', 'pp-sr__understood');
      row.append(el('span', 'pp-sr__lbl', msg('understood')));
      understood.forEach(c => {
        const next = core.composeQuery(intent, lang, x => core.without(x, c.kind));
        if (understood.length === 1) row.append(el('span', 'pp-sr-chip pp-sr-chip--static', c.label));
        else row.append(chip(c.label, urlFor(next, state.sort).href, { on: true, q: next }));
      });
      body.append(row);
    }

    // notes (corrections, dropped words, out of range, not carried …)
    const notes = result.notes.filter(n => n.code !== 'clarify' || result.mode === 'clarify');
    if (notes.length) {
      const box = el('div', 'pp-sr__notes');
      notes.forEach(n => {
        const p = Object.assign({}, n);
        if (n.quality) p.quality = msg(n.quality);
        box.append(el('p', 'pp-sr__note', msg(n.code, p)));
      });
      body.append(box);
    }

    // refinement chips over the full result set
    if (result.facets.length) {
      const facets = el('div', 'pp-sr__facets');
      facets.append(el('p', 'pp-sr__lbl', msg('refine')));
      result.facets.forEach(f => {
        const group = el('div', 'pp-sr__facet');
        group.setAttribute('role', 'group');
        const name = el('span', 'pp-sr__facet-name', msg(f.kind));
        name.id = 'pp-sr-f-' + f.kind;
        group.setAttribute('aria-labelledby', name.id);
        group.append(name);
        f.values.forEach(v => {
          // 1.2.0: v.q is the text the engine searched to count v.count (the chip's page shows that many)
          const next = typeof v.q === 'string' && v.q ? v.q : core.composeQuery(intent, lang, x => core.withFacet(x, f.kind, v.key));
          group.append(chip(core.facetLabel(f.kind, v.key, lang, intent), urlFor(next, state.sort).href, { count: v.count, q: next }));
        });
        facets.append(group);
      });
      body.append(facets);
    }

    // main list + load more
    const list = listEl('pp-sr-list');
    list.setAttribute('aria-labelledby', 'pp-sr-count');
    const shown = Math.min(state.shown, result.main.length);
    list.append(grid(result.main.slice(0, shown), 0, true));
    if (result.main.length) body.append(list);
    const more = el('div', 'pp-load-more-wrap pp-sr__more');
    const live = el('p', 'visually-hidden');
    live.setAttribute('role', 'status'); live.setAttribute('aria-live', 'polite'); live.id = 'pp-sr-live';
    if (result.main.length > shown) {
      const btn = el('button', 'pp-load-more-btn');
      btn.type = 'button';
      btn.append(el('span', 'pp-load-more-label', msg('loadMore')));
      btn.setAttribute('aria-controls', 'pp-sr-list');
      const counter = el('div', 'pp-load-more-count', msg('of', { shown: shown, total: result.main.length }));
      btn.addEventListener('click', () => loadMore(list, btn, counter, live));
      more.append(btn, counter);
    }
    more.append(live);
    body.append(more);

    // nothing sure: why, what does exist, and the contact options
    if (!result.main.length && result.mode !== 'clarify') body.append(emptyState(result));

    // groups apart from the sure matches
    if (result.quality.length && !result.main.length) body.append(group('quality', result.quality));
    if (result.unknown.length) body.append(group('unknown', result.unknown));
    if (result.related.length) body.append(group('related', result.related));

    if (gen !== state.gen) return;
    root.replaceChildren(body);
    updateTitle(result);
    if (!initial) live.textContent = count.textContent;
  }
  function group(kind, items) {
    const sec = el('section', 'pp-sr__group pp-sr__group--' + kind);
    const hid = 'pp-sr-g-' + kind;
    sec.setAttribute('aria-labelledby', hid);
    const h = el('h2', 'pp-sr__group-title', msg(kind === 'unknown' ? 'unknownTitle' : kind === 'related' ? 'relatedTitle' : 'qualityTitle', { n: items.length }));
    h.id = hid; h.tabIndex = -1;
    sec.append(h);
    if (kind === 'unknown') {
      const what = new Set();
      items.forEach(it => ((state.result && it.a && evaluateUnknown(it)) || []).forEach(k => what.add(k)));
      sec.append(el('p', 'pp-sr__hint', msg('unknownHint', { what: [...what].map(k => msg('unknown_' + k)).join(', ') || msg('unknown_side') })));
    }
    const ul = listEl();
    const first = items.slice(0, GROUP).map(it => kind === 'unknown' ? Object.assign({}, it, { unconfirmed: evaluateUnknown(it) }) : it);
    ul.append(grid(first, 0, false));
    sec.append(ul);
    if (items.length > GROUP) {
      const btn = el('button', 'pp-sr__group-more', msg('loadMore') + ' (' + (items.length - GROUP) + ')');
      btn.type = 'button';
      btn.addEventListener('click', () => {
        const rest = items.slice(GROUP).map(it => kind === 'unknown' ? Object.assign({}, it, { unconfirmed: evaluateUnknown(it) }) : it);
        const before = ul.children.length;
        ul.append(grid(rest, before, false));
        btn.remove();
        const firstNew = ul.children[before] && ul.children[before].querySelector('a[id]');
        if (firstNew) firstNew.focus();
      });
      sec.append(btn);
    }
    return sec;
  }
  function evaluateUnknown(item) {
    const ev = state.core.evaluate(state.result.intent, item);
    return (ev && ev.unknown) || [];
  }
  function sameQuery(a, b) { return String(a || '').replace(/\s+/g, ' ').trim().toLowerCase() === String(b || '').replace(/\s+/g, ' ').trim().toLowerCase(); }
  function emptyState(result) {
    const box = el('div', 'pp-sr__empty');
    const relax = result.relax || [];
    if (relax.gens && relax.gens.length) {
      const row = el('div', 'pp-sr__facet');
      row.append(el('span', 'pp-sr__facet-name', msg('gensTitle')));
      relax.gens.forEach(g => {
        const next = typeof g.q === 'string' && g.q ? g.q : state.core.composeQuery(result.intent, lang, x => state.core.withFacet(x, 'gen', g.key));
        if (!sameQuery(next, state.q)) row.append(chip(state.core.facetLabel('gen', g.key, lang), urlFor(next, state.sort).href, { count: g.count, q: next }));
      });
      box.append(row);
    }
    const alt = relax.filter(r => r.kind !== 'year' || !(relax.gens && relax.gens.length)).slice(0, 4);
    if (alt.length) {
      const row = el('div', 'pp-sr__facet');
      row.append(el('span', 'pp-sr__facet-name', msg('tryInstead')));
      alt.forEach(r => {
        const next = typeof r.q === 'string' ? r.q : state.core.composeQuery(r.intent, lang);
        // 1.2.0: never the current query again (that suggestion was a loop)
        if (next && next.length >= 2 && !sameQuery(next, state.q)) row.append(chip(next, urlFor(next, state.sort).href, { count: r.count, q: next }));
      });
      if (row.children.length > 1) box.append(row);
    }
    const tpl = document.getElementById('pp-se-tpl');
    if (tpl && tpl.content) box.append(tpl.content.cloneNode(true));
    else box.append(el('p', 'pp-sr__note', msg('none')));
    return box;
  }
  function loadMore(list, btn, counter, live) {
    const main = state.result.main;
    const from = list.children.length;
    const next = main.slice(from, from + PAGE);
    list.append(grid(next, from, false));
    state.shown = list.children.length;
    history.replaceState(Object.assign({}, history.state, { ppShown: state.shown }), '');
    counter.textContent = msg('of', { shown: state.shown, total: main.length });
    // S7: keyboard focus moves to the first new card (Tab continues through the new ones); the change is announced
    const firstNew = list.children[from] && list.children[from].querySelector('a[id]');
    live.textContent = state.shown >= main.length ? msg('added', { n: next.length, shown: state.shown, total: main.length }) + ' ' + msg('allShown', { total: main.length }) : msg('added', { n: next.length, shown: state.shown, total: main.length });
    if (state.shown >= main.length) btn.remove();
    if (firstNew) firstNew.focus({ preventScroll: false });
  }
  function updateTitle(result) {
    if (!cfg.title) return;
    document.title = cfg.title.replace('__Q__', state.q).replace('__N__', String(result.total));
  }

  /* ------------------------------------------------------------------ native fallback: accessible load more */
  function enhanceNativeLoadMore() {
    const wrap = document.querySelector('[data-pp-native] .pp-load-more-wrap');
    if (wrap && !wrap.querySelector('[role="status"]')) {
      const live = el('p', 'visually-hidden');
      live.setAttribute('role', 'status'); live.setAttribute('aria-live', 'polite');
      wrap.append(live);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();